// AIHELP1 (docs/build-briefs/AIHELP1-willy-ai-support-chat.md; DEC-574): the pure parts of Willy, the AI support chat. No DOM, so a
// plain node:test file can import it. The words are fixed here; nothing the server or the model says is shown except the answer
// text itself, which the panel sets with textContent.

import type { Route } from "./screenRoute.ts";
import { BODY_MAX, TITLE_MAX } from "./supportHelpModel.ts";

export const WILLY_GREETING = "Hi, I'm Willy. How can I help?";
export const WILLY_UNAVAILABLE_LINE = "Willy is unavailable right now.";
export const WILLY_TRY_AGAIN_LINE = "Willy could not answer. Try again.";
export const WILLY_BUSY_LINE = "Willy is looking that up...";
export const WILLY_RATE_LIMITED_LINE = "You have asked Willy a lot just now. Try again in a while, or contact support.";
export const WILLY_EMPTY_LINE = "Type a question for Willy.";
export const WILLY_QUESTION_MAX = 1000;
/** The server accepts at most this many messages per turn. */
export const WILLY_HISTORY_MAX = 12;

export interface WillySource {
  readonly slug: string;
  readonly section?: string;
  readonly title: string;
}
export interface WillyMessage {
  /** Who spoke: the person (user) or Willy (assistant). */
  readonly from: "user" | "assistant";
  readonly text: string;
  readonly source?: WillySource | null;
  /** False when the guides did not cover the question. */
  readonly answered?: boolean;
}
export interface WillyAnswerDto {
  answer: string;
  answered: boolean;
  source: WillySource | null;
}

export function isWillyAnswer(body: unknown): body is WillyAnswerDto {
  if (typeof body !== "object" || body === null) return false;
  const { answer, answered, source } = body as Partial<WillyAnswerDto>;
  if (typeof answer !== "string" || answer.trim() === "" || typeof answered !== "boolean") return false;
  if (source === null) return true;
  return typeof source === "object" && typeof source.slug === "string" && typeof source.title === "string" &&
    (source.section === undefined || typeof source.section === "string");
}

/** What the server is sent: the last turns only, plain text. */
export function historyForServer(messages: readonly WillyMessage[]): Array<{ from: "user" | "assistant"; text: string }> {
  return messages.slice(-WILLY_HISTORY_MAX).map((m) => ({ from: m.from, text: m.text }));
}

/** The words after a question is typed, or undefined when it is fine to send. */
export function questionError(text: string): string | undefined {
  if (text.trim() === "") return WILLY_EMPTY_LINE;
  if (text.length > WILLY_QUESTION_MAX) return "Keep your question under 1,000 characters.";
  return undefined;
}

/** The Get help question form, opened with the conversation so the person does not retype it. The most recent lines are kept
 * when the whole conversation does not fit. */
export function supportPrefill(messages: readonly WillyMessage[]): { title: string; body: string } {
  const lastQuestion = [...messages].reverse().find((m) => m.from === "user")?.text.trim() ?? "";
  const title = lastQuestion === "" ? "Question after chatting with Willy" : lastQuestion.replace(/\s+/g, " ");
  const lines = messages.map((m) => `${m.from === "user" ? "Me" : "Willy"}: ${m.text.trim()}`);
  const head = "I asked Willy and I am still stuck.\n\n";
  let body = "";
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const next = `${lines[i]}\n${body}`;
    if (head.length + next.length > BODY_MAX) break;
    body = next;
  }
  return { title: title.slice(0, TITLE_MAX), body: (head + body).trimEnd().slice(0, BODY_MAX) };
}

/** Floor and lounger screens never get Willy (the tabs list and a ticket); a screen the address does not name gets none either. */
export function willyRouteAllowed(route: Route | undefined): boolean {
  if (route === undefined) return false;
  return route.kind !== "tabs" && route.kind !== "ticket";
}

/** WILLYFACE1 (DEC-591): the letters in the person's round avatar. First letter of the first name plus first letter of the last
 * name, uppercase; one letter when there is one name; empty when there is no name (the avatar then shows a person glyph). */
export function personInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== "");
  const first = words[0];
  if (first === undefined) return "";
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  const letter = (word: string): string => Array.from(word)[0]?.toLocaleUpperCase() ?? "";
  return letter(first) + (last === undefined ? "" : letter(last));
}
